import { multiplyRems } from 'application/theme/utils'
import React, { FC, useEffect, useState } from 'react'
import { IMaskMixin } from 'react-imask'
import styled, { useTheme } from 'styled-components'
import { options } from 'ui/phone-input/options'

import { getProjectName } from '../../lib/get-project-name'
import { Icon } from '../icon'
import { ScrollableContainer } from '../scrollable-container'
import { Input } from './Input'
import { OptionProps, PhoneInputProps } from './types'

const MaskedStyledInput = IMaskMixin(({ inputRef, ...props }) => {
  return <Input {...props} inputRef={inputRef} inputMode={'numeric'} />
})

export const PhoneInput: FC<PhoneInputProps> = ({ maskCountryCode = 'ru', onChange, required, ...restProps }) => {
  const [showList, setShowList] = useState<boolean>(false)
  const [selected, setSelected] = useState<OptionProps>(options[maskCountryCode])

  const onCountyChange = (option: OptionProps) => {
    setShowList(!showList)
    setSelected(option)
  }

  useEffect(() => onChange(''), [selected])

  const [isFocused, setIsFocused] = useState(false)
  const theme = useTheme()

  return (
    <StyledPhoneInput>
      <StyledPhoneInputInner>
        <Selector onClick={() => setShowList(!showList)}>
          <ImageWrapper>
            <img src={`data:image/svg+xml;base64,${selected?.flag}`} alt={selected?.name} />
          </ImageWrapper>
          <Name>{selected?.code}</Name>
          <Arrow />
        </Selector>
        <List showList={showList}>
          <ScrollableContainer maxHeight={'200px'}>
            {Object.values(options).map((option) => (
              <Option key={option.code} onClick={() => onCountyChange(option)}>
                <ImageWrapper>
                  <img src={`data:image/svg+xml;base64,${option.flag}`} alt={option.name} />
                </ImageWrapper>
                <Name>{option.name}</Name>
                <Code>{option.phoneCode}</Code>
              </Option>
            ))}
          </ScrollableContainer>
        </List>
        <MaskedStyledInput
          onClick={() => setShowList(false)}
          mask={selected.phoneMask}
          lazy={false}
          onAccept={(value) => {
            onChange(value)
          }}
          onComplete={() => {}}
          onBlur={restProps.onBlur}
          value={restProps.value}
          label={restProps.label}
          name={restProps.name}
          error={restProps.error}
          disabled={restProps.disabled}
          block={true}
          isFocused={isFocused}
          setIsFocused={setIsFocused}
          required={required}
        />
      </StyledPhoneInputInner>

      {restProps.error && !isFocused && (
        <>
          <ErrorMessage>{restProps.error}</ErrorMessage>
          <IconError>
            <Icon name="info-bmw" color={theme.palette.alert} />
          </IconError>
        </>
      )}
    </StyledPhoneInput>
  )
}

const StyledPhoneInput = styled.div`
  display: flex;
  flex-wrap: wrap;
  width: 100%;
  position: relative;
`

const StyledPhoneInputInner = styled.div`
  display: flex;
  width: 100%;
`

const Selector = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: ${({ theme }) => theme.spacing[1]};
  flex: ${({ theme }) => `${theme.spacing[10]} ${theme.spacing[0]} ${theme.spacing[24]}`};
  border: 1px solid ${({ theme }) => theme.palette.grey[200]};
  border-right: none;
  border-radius: ${() => getProjectName() === 'avito' && '16px 0 0 16px;'};
  background: ${({ theme }) => theme.palette.white};
  flex-shrink: 0;
  flex-grow: 0;
  cursor: pointer;
`

const Arrow = styled.div`
  border-style: solid;
  border-width: 0.5rem 0.5rem 0;
  border-color: ${({ theme }) => theme.palette.grey[300]} transparent transparent;
`

const List = styled.div<{ showList: boolean }>`
  display: ${({ showList }) => (showList ? 'block' : 'none')};
  position: absolute;
  width: 100%;
  top: 100%;
  z-index: 101;

  max-height: ${({ theme }) => theme.spacing[64]};
  background: ${({ theme }) => theme.palette.white};
  overflow: auto;

  border: 1px solid ${({ theme }) => theme.palette.grey[200]};
  border-radius: 0.5rem;
  padding: ${({ theme }) => `${theme.spacing[1]} ${theme.spacing[0]}`};

  max-width: ${({ theme }) => multiplyRems(theme.spacing[1], 112)};
`
const Option = styled.div`
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing[3]};
  color: ${({ theme }) => theme.palette.grey[800]};

  &:hover {
    cursor: pointer;
    background: ${({ theme }) => theme.palette.grey[200]};
  }
`
const ImageWrapper = styled.div`
  display: flex;
  align-items: center;
  width: ${({ theme }) => theme.spacing[5]};
  margin-right: ${({ theme }) => theme.spacing[1]};
  overflow: hidden;
  img {
    width: 20px;
    height: 20px;
    object-fit: contain;
  }
`
const Name = styled.span`
  margin-right: ${({ theme }) => theme.spacing[1]};
`
const Code = styled.span`
  margin-left: auto;
`

const ErrorMessage = styled.p`
  width: 100%;
  bottom: ${({ theme }) => ` -${getProjectName() === 'avito' ? theme.spacing[4] : theme.spacing[6]}`};
  margin: 0;
  color: ${({ theme }) => theme.palette.alert};
  padding: 4px 0;
  ${({ theme }) => theme.textStyles.p3};
`

const IconError = styled.i`
  position: absolute;
  top: -11px;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ theme }) => theme.spacing[16]};
  height: 100%;
  cursor: pointer;
`
